import {
  type SubscriptionStatus,
  type ModuleOrgType,
  type TenantModuleActivationSummary,
  computeTenantEnabledModules,
  isHrWorkerOnlyGoLiveScope,
  normalizeGoLiveScope,
  resolveEffectiveBillingPlan,
  isSubscriptionBlocked,
  type VisibleGoLiveScope,
} from '../billing';
import { normaliseOrgType, resolveLegacyPlanKey } from '../plans';
import {
  getOnboardingProgressSummary,
  type OnboardingProgress,
} from '../tenant-onboarding-progress';
import { getTenantHiddenNavigationPathsFromBrandConfig } from '../navigation/tenant-navigation';
import { FRONTLINE_SELF_SERVICE_ONLY_KEY } from '../navigation/frontline-self-service';
import {
  getStepsForTenant,
  type OrgType as OnboardingOrgType,
  type PlanTier as OnboardingPlanTier,
} from '../../app/(dashboard)/onboarding/lib/step-config';
import type { CustomerLegalState } from '../customer-legal-shared';
import type { MfaReminderSeed } from './mfa-reminder';
import { isComplianceTrialAccessScopeTenant } from '../compliance-trial-access';

/**
 * The gate-critical subset the server resolves up front and passes into the
 * shell as initial props (loading-UX phase 2). Seeding these means the shell's
 * first render already satisfies the module + customer-legal gates — no
 * "Opening workspace" full-screen loader, no ModuleGate inline loader.
 *
 * Declared in this framework-neutral module so the client shell can import the
 * type without pulling in the server-only resolver.
 */
export interface InitialShellContext {
  /** Cosmetic user preference, admitted with the same server identity as this context. */
  sidebarCollapsed?: boolean | null;
  mfaReminder?: MfaReminderSeed;
  billingPlan: string | null;
  enabledModules: string[] | null;
  moduleLoadError: boolean;
  isDemoTenant: boolean;
  /** Demo tenants only: plan-derived modules that gate payroll paths. */
  demoPlanModules: string[] | null;
  isCiFree: boolean;
  isSubBlocked: boolean;
  filterPreviewSettingsNav: boolean;
  /** Active launch profile used to keep primary navigation compact for go-live tenants. */
  visibleGoLiveScope: VisibleGoLiveScope;
  /** Tenant-level navigation curation applied after hard gates. */
  tenantHiddenNavigationPaths: string[];
  /** Restrict frontline navigation to roster/shift/leave self-service routes. */
  frontlineSelfServiceOnly: boolean;
  /** Tenant/global `shift_swaps` resolution, read on the server and passed to client navigation. */
  shiftSwapsEnabled: boolean;
  payrollRebuildOn?: boolean;
  /** Resolved customer-legal acceptance state, or null when not yet confirmed. */
  customerLegalState: CustomerLegalState | null;
  /**
   * True only when the server definitively resolved the customer-legal state
   * (or it does not apply to this role). When false, the client must resolve it
   * before lifting the gate — preserving fail-closed semantics for the legal
   * acceptance requirement.
   */
  customerLegalLoaded: boolean;
  /**
   * Tenant-level IANA timezone (tenant_configs.timezone), or null when it could
   * not be resolved. Plumbed to the client so the tenant-wide view ("All
   * Facilities", currentFacility === null) resolves the tenant timezone instead
   * of the Australia/Sydney hardcode. When a facility IS selected, the facility
   * timezone/state still wins (see resolveTimezone).
   */
  tenantTimezone: string | null;
}

/**
 * Loading-UX phase 2 — single source of truth for the dashboard shell's
 * module/entitlement derivation.
 *
 * Previously this computation lived inline in the dashboard-shell.tsx client
 * useEffect, which meant first paint was gated behind a client→server round
 * trip ("Opening workspace" full-screen loader). The server now resolves the
 * same state up front (see server-shell-context.ts) and seeds the shell so the
 * gate is already satisfied. Because BOTH the server resolver and the client
 * refresh-effect call THIS function, the entitlement gating cannot drift
 * between the two — a hard requirement for a regulated platform.
 *
 * This module is intentionally framework-neutral: no 'use client', no
 * 'server-only', no Supabase imports. It is a pure function of a tenant-config
 * object. Side effects (setCachedOrgType, setState, the new-tenant data probe)
 * stay with the caller.
 */

/**
 * How the caller should reconcile org labels after derivation — mirrors the
 * three distinct branches of the original useEffect exactly:
 *  - 'set'        → resolveOrgLabels(orgType, true)  (normal path)
 *  - 'markLoaded' → keep current labels, mark isLoaded:true (config === null)
 *  - 'none'       → do not touch labels (subscription-blocked early return)
 */
export type OrgLabelsMode = 'set' | 'markLoaded' | 'none';

export interface OnboardingDerivation {
  hideWizard: boolean;
  onboardingCompleted: boolean;
  hasCurrentStep: boolean;
  hasPlanOrTier: boolean;
  summary: {
    resolvedSteps: number;
    totalSteps: number | undefined;
    deferredSteps: number;
    blockedSteps: number;
  };
}

export interface DerivedShellModuleState {
  billingPlan: string | null;
  enabledModules: string[] | null;
  moduleLoadError: boolean;
  isDemoTenant: boolean;
  /** Demo tenants only: plan-derived modules that gate payroll paths. */
  demoPlanModules: string[] | null;
  isCiFree: boolean;
  isSubBlocked: boolean;
  filterPreviewSettingsNav: boolean;
  visibleGoLiveScope: VisibleGoLiveScope;
  tenantHiddenNavigationPaths: string[];
  frontlineSelfServiceOnly: boolean;
  orgType: string | undefined;
  orgLabelsMode: OrgLabelsMode;
  /**
   * Onboarding banner inputs. Null for the fail-closed (config === null) and
   * subscription-blocked branches — matching the original, which only computed
   * onboarding state on the normal path. Non-gating.
   */
  onboarding: OnboardingDerivation | null;
}

const CI_FREE_SETTINGS_PATHS = [
  '/settings/organisation',
  '/settings/users',
  '/settings/security',
  '/settings/system',
];

/** Fail-closed shape — used when the tenant config cannot be read. */
function failClosed(): DerivedShellModuleState {
  return {
    billingPlan: null,
    enabledModules: [],
    moduleLoadError: true,
    isDemoTenant: false,
    demoPlanModules: null,
    isCiFree: false,
    isSubBlocked: false,
    filterPreviewSettingsNav: false,
    visibleGoLiveScope: null,
    tenantHiddenNavigationPaths: [],
    frontlineSelfServiceOnly: false,
    orgType: undefined,
    orgLabelsMode: 'markLoaded',
    onboarding: null,
  };
}

export function deriveShellModuleState(
  config: Record<string, unknown> | null,
): DerivedShellModuleState {
  // Fail closed when the tenant config cannot be read. Treating a missing
  // config as "all modules enabled" would leak paid modules and
  // subscription-blocked routes on hard navigations.
  if (!config) {
    return failClosed();
  }

  const isDemoTenant = config.isDemo === true;
  const brandConfig = config.brandConfig as Record<string, unknown> | null;
  const goLiveScope = brandConfig?.visibleGoLiveScope;
  const visibleGoLiveScope = normalizeGoLiveScope(goLiveScope);
  const tenantHiddenNavigationPaths = getTenantHiddenNavigationPathsFromBrandConfig(brandConfig);
  const frontlineSelfServiceOnly = brandConfig?.[FRONTLINE_SELF_SERVICE_ONLY_KEY] === true;
  const selectedPackages =
    (config.selectedPackages as string[]) ?? (brandConfig?.selectedPackages as string[]) ?? [];
  const filterPreviewSettingsNav = isHrWorkerOnlyGoLiveScope(goLiveScope);

  const plan = brandConfig?.plan as string | undefined;
  const tier = config.subscriptionTier as string | undefined;
  const hideOnboardingWizard = isHrWorkerOnlyGoLiveScope(goLiveScope);
  const isCiFree = plan === 'CI_FREE' || tier === 'ci-free';

  // Block access for canceled/suspended subscriptions (before module entitlement).
  // Demo tenants are intentionally all-access for controlled testing and must
  // not inherit billing-state gates from copied/provisioning data.
  const subscriptionStatus = config.subscriptionStatus as string | undefined;
  const isComplianceTrial = isComplianceTrialAccessScopeTenant({
    billingPlan: config.billingPlan as string | null,
    subscriptionStatus,
    isDemo: isDemoTenant,
    selectedPackages,
    brandConfig,
  });
  if (
    !isDemoTenant &&
    subscriptionStatus &&
    isSubscriptionBlocked(subscriptionStatus as SubscriptionStatus)
  ) {
    return {
      billingPlan: (config.billingPlan as string | null) ?? null,
      enabledModules: [],
      moduleLoadError: false,
      isDemoTenant,
      demoPlanModules: null,
      isCiFree,
      isSubBlocked: true,
      filterPreviewSettingsNav,
      visibleGoLiveScope,
      tenantHiddenNavigationPaths,
      frontlineSelfServiceOnly,
      orgType: undefined,
      orgLabelsMode: 'none',
      onboarding: null,
    };
  }

  const effectivePlan = isComplianceTrial
    ? null
    : resolveEffectiveBillingPlan({
        billingPlan: config.billingPlan as string | null,
        subscriptionTier: tier,
        legacyPlan: plan,
      });

  // Org-type filtering: normalise so mis-provisioned tenants (missing/MIXED
  // orgType, or one that disagrees with the plan) still render the right nav.
  const storedOrgType = brandConfig?.orgType as string | undefined;
  const planKeyForOrgType = effectivePlan || plan || tier || '';
  const orgType = isComplianceTrial
    ? storedOrgType
    : planKeyForOrgType
      ? normaliseOrgType(planKeyForOrgType, storedOrgType as never)
      : storedOrgType;

  // Onboarding summary (pure given config).
  const onboardingCompleted = brandConfig?.onboardingCompleted === true;
  const onboardingProgress = brandConfig?.onboardingProgress as OnboardingProgress | undefined;
  const hasPlanOrTier = !!(config.billingPlan || tier || plan);
  const setupPlan = resolveLegacyPlanKey(
    (effectivePlan ?? plan ?? tier ?? 'ESSENTIALS') as string,
  ) as OnboardingPlanTier;
  const setupOrgType =
    orgType === 'HOME_CARE' || orgType === 'MIXED' || orgType === 'RESIDENTIAL'
      ? (orgType as OnboardingOrgType)
      : 'RESIDENTIAL';
  const setupEnabledModules = isDemoTenant
    ? null
    : computeTenantEnabledModules({
        billingPlan: effectivePlan ?? null,
        complianceTrial: isComplianceTrial,
        selectedPackages,
        orgType: (orgType as ModuleOrgType | undefined) ?? null,
        systemEnabledModules: (brandConfig?.systemEnabledModules as string[]) ?? [],
        adminDisabledModules: (brandConfig?.adminDisabledModules as string[]) ?? [],
        legacyEnabledModules: (brandConfig?.enabledModules as string[]) ?? null,
        moduleActivations:
          (config.moduleActivations as TenantModuleActivationSummary[] | undefined) ?? [],
        goLiveScope,
      });
  const setupSteps = getStepsForTenant(setupPlan, setupOrgType, {
    enabledModules: setupEnabledModules,
  });
  const setupSummary = getOnboardingProgressSummary(
    onboardingProgress ?? null,
    setupSteps.length,
    setupSteps.map((step) => step.id),
  );

  // Demo tenants still resolve their plan modules: demo all-access never
  // covers payroll, which is gated by the plan instead.
  const resolvedModules = computeTenantEnabledModules({
    billingPlan: effectivePlan ?? null,
    complianceTrial: isComplianceTrial,
    selectedPackages,
    orgType: (orgType as ModuleOrgType | undefined) ?? null,
    systemEnabledModules: (brandConfig?.systemEnabledModules as string[]) ?? [],
    adminDisabledModules: (brandConfig?.adminDisabledModules as string[]) ?? [],
    legacyEnabledModules: (brandConfig?.enabledModules as string[]) ?? null,
    moduleActivations:
      (config.moduleActivations as TenantModuleActivationSummary[] | undefined) ?? [],
    goLiveScope,
  });

  let enabledModules: string[] | null;
  let moduleLoadError = false;
  if (isDemoTenant) {
    enabledModules = null;
  } else if (resolvedModules) {
    let finalModules = resolvedModules;
    // For restricted plans (CI-free), add allowed settings paths so the
    // sidebar filtering still surfaces them.
    if (isCiFree) {
      finalModules = [...new Set([...resolvedModules, ...CI_FREE_SETTINGS_PATHS])];
    }
    enabledModules = finalModules;
  } else {
    enabledModules = [];
    moduleLoadError = true;
  }

  return {
    billingPlan: (config.billingPlan as string | null) ?? null,
    enabledModules,
    moduleLoadError,
    isDemoTenant,
    demoPlanModules: isDemoTenant ? (resolvedModules ?? []) : null,
    isCiFree,
    isSubBlocked: false,
    filterPreviewSettingsNav,
    visibleGoLiveScope,
    tenantHiddenNavigationPaths,
    frontlineSelfServiceOnly,
    orgType,
    orgLabelsMode: 'set',
    onboarding: {
      hideWizard: hideOnboardingWizard,
      onboardingCompleted,
      hasCurrentStep: !!onboardingProgress?.currentStep,
      hasPlanOrTier,
      summary: {
        resolvedSteps: setupSummary.resolvedSteps,
        totalSteps: setupSummary.totalSteps,
        deferredSteps: setupSummary.deferredSteps,
        blockedSteps: setupSummary.blockedSteps,
      },
    },
  };
}
